import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Load, with the two properties a monitoring console actually needs:
 *
 *   OUT-OF-ORDER RESPONSES ARE DROPPED. A ticket counter means a slow first
 *   request cannot overwrite a fast second one -- on this console that would
 *   paint a stale incident board over a fresh one, which is worse than showing
 *   nothing.
 *
 *   REFRESH KEEPS THE OLD DATA ON SCREEN. `refreshing` says the rows are from
 *   the previous poll; blanking the board every few seconds makes it unreadable.
 *
 *   A NEW QUESTION IS NOT A REFRESH. When the deps change, the rows on screen
 *   answer the PREVIOUS question until the new answer lands, and `stale` says
 *   so -- separately from `refreshing`, so a search can show that it is running
 *   without the same spinner turning on for every background poll.
 *
 *   A POLL NEVER STACKS ON THE ONE BEFORE IT. The interval fires on a clock, not
 *   on the previous response, so an endpoint slower than its own interval used
 *   to start a second request before the first had landed -- and then a third.
 *   That is worse than wasteful, because of the ticket above: each new request
 *   invalidates the one still in flight, so EVERY response is discarded as
 *   stale and the screen never leaves "Loading…". A Queues screen whose Redis
 *   had gone slow did not render slowly, it did not render at all, and the
 *   requests piled onto the thing that was already struggling. A tick that
 *   arrives with a request still out is therefore dropped; the next one covers
 *   it, and a slow endpoint degrades to a slower refresh rather than to nothing.
 *
 *   A FAILED POLL KEEPS THE ROWS IT HAD. `error` says the latest load failed;
 *   `data` is still the last good answer and `updatedAt` says when it came, so a
 *   screen can say "could not refresh, showing what was read 40s ago" over its
 *   rows instead of swapping a board somebody is triaging for a red line
 *   (components/LoadIssue.tsx).
 *
 *   A HIDDEN TAB DOES NOT POLL. The console is one of twenty tabs, open all
 *   day; every screen's clock firing in a tab nobody can see was a request a
 *   few seconds per screen against the API and, through it, the CRM -- and a
 *   throttled background timer meant the screen came back showing a minute-old
 *   answer with nothing to say so. The clock skips while hidden, and coming
 *   back reads at once if a tick was missed. (The shell's queue counts are not
 *   built on this, on purpose: their job is to notice somebody waiting while
 *   nobody is looking.)
 *
 * Same shape as the CRM console's src/lib/useLoad.ts, deliberately.
 */
export function useLoad<T>(load: () => Promise<T>, deps: unknown[] = [], intervalMs?: number) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stale, setStale] = useState(true);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const ticket = useRef(0);
  const inFlight = useRef(false);
  const lastLoadedAt = useRef(0);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the caller's deps decide when a new `load` applies; it is a fresh closure on every render
  const run = useCallback(
    async (isRefresh: boolean) => {
      // A load caused by changed deps must always go; only a clock tick is
      // droppable, because the same request is already on its way.
      if (isRefresh && inFlight.current) return;
      const mine = ++ticket.current;
      inFlight.current = true;
      if (isRefresh) setRefreshing(true);
      else setStale(true);
      try {
        const next = await load();
        if (mine !== ticket.current) return; // a newer request already answered
        setData(next);
        setError(null);
        lastLoadedAt.current = Date.now();
        setUpdatedAt(lastLoadedAt.current);
      } catch (caught) {
        if (mine !== ticket.current) return;
        setError(caught as Error);
      } finally {
        // Only the newest request clears the flag: an older one landing late
        // must not hand the clock a green light the live request has not given.
        if (mine === ticket.current) {
          inFlight.current = false;
          setLoading(false);
          setRefreshing(false);
          setStale(false);
        }
      }
    },
    deps,
  );

  useEffect(() => {
    void run(false);
    if (!intervalMs) return;
    const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const timer = setInterval(() => {
      if (!hidden()) void run(true);
    }, intervalMs);
    // Back in view: read now if the clock went by while hidden, rather than
    // showing an old answer until the next tick.
    const onVisibility = () => {
      if (!hidden() && Date.now() - lastLoadedAt.current >= intervalMs) void run(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [run, intervalMs]);

  return { data, error, loading, refreshing, stale, updatedAt, reload: () => run(true) };
}
